import React from 'react';
import { Star, StarOff, TrendingUp } from 'lucide-react';
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";

const SP_PER_LEVEL = [0, 100, 250, 500, 1000, 2000]; // SP needed to reach each level

export default function SkillProgressionDisplay({ skills = [] }) {
  const renderStars = (level) => {
    return (
      <div className="flex gap-0.5">
        {[1, 2, 3, 4, 5].map((star) => (
          star <= level ? (
            <Star key={star} className="w-4 h-4 fill-amber-400 text-amber-400" />
          ) : (
            <StarOff key={star} className="w-4 h-4 text-slate-600" />
          )
        ))}
      </div>
    );
  };

  const getLevelLabel = (level) => {
    const labels = {
      1: 'Novice',
      2: 'Beginner',
      3: 'Intermediate',
      4: 'Advanced',
      5: 'Expert'
    };
    return labels[level] || 'Unknown';
  };

  const getSpForNextLevel = (currentLevel, currentXp) => {
    if (currentLevel >= 5) return { needed: 0, progress: 100 };
    
    const currentLevelThreshold = SP_PER_LEVEL[currentLevel];
    const nextLevelThreshold = SP_PER_LEVEL[currentLevel + 1];
    const spNeeded = nextLevelThreshold - currentLevelThreshold;
    const spProgress = currentXp - currentLevelThreshold;
    const progressPercent = (spProgress / spNeeded) * 100;
    
    return {
      needed: spNeeded,
      current: spProgress,
      progress: progressPercent
    };
  };

  const getBenefits = (level) => {
    const benefits = {
      1: 'Can participate in basic activities',
      2: 'Can lead simple tasks',
      3: 'Can handle complex projects independently',
      4: 'Can mentor others and design solutions',
      5: 'Expert authority - can innovate and define standards'
    };
    return benefits[level] || '';
  };

  if (!skills || skills.length === 0) {
    return (
      <div className="text-center py-8 border border-dashed border-slate-800 rounded-lg">
        <p className="text-sm text-slate-500">No skills to display</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {skills.map((skill, index) => {
        const progression = getSpForNextLevel(skill.level, skill.xp || 0);
        const isMaxLevel = skill.level >= 5;

        return (
          <Card key={index} className="bg-slate-950 border-slate-800">
            <CardContent className="p-4 space-y-3">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h4 className="text-slate-300 font-semibold">{skill.name}</h4>
                    <Badge variant="outline" className="text-xs bg-amber-500/10 text-amber-400 border-amber-500/30">
                      Level {skill.level} - {getLevelLabel(skill.level)}
                    </Badge>
                  </div>
                  {renderStars(skill.level)}
                </div>
                
                <div className="text-right">
                  <div className="text-xs text-slate-500">Total SP</div>
                  <div className="text-lg font-bold text-indigo-400 font-mono">{skill.xp || 0}</div>
                </div>
              </div>

              {!isMaxLevel ? (
                <div className="space-y-2">
                  <div className="flex justify-between text-xs">
                    <span className="text-slate-500">Progress to Level {skill.level + 1}</span>
                    <span className="text-indigo-400 font-mono">
                      {progression.current} / {progression.needed} SP
                    </span>
                  </div>
                  <Progress 
                    value={progression.progress} 
                    className="h-2 bg-slate-800" 
                    indicatorClassName="bg-indigo-500"
                  />
                </div>
              ) : (
                <div className="flex items-center gap-2 text-amber-400 text-xs">
                  <Star className="w-4 h-4 fill-amber-400" />
                  <span className="font-semibold">MAX LEVEL ACHIEVED</span>
                </div>
              )}

              <div className="pt-2 border-t border-slate-800">
                <div className="flex items-start gap-2">
                  <TrendingUp className="w-4 h-4 text-emerald-400 mt-0.5" />
                  <div>
                    <div className="text-xs font-semibold text-emerald-400 mb-1">
                      Level {skill.level} Benefits:
                    </div>
                    <p className="text-xs text-slate-400">{getBenefits(skill.level)}</p>
                    {!isMaxLevel && (
                      <p className="text-xs text-slate-500 mt-2">
                        <span className="text-indigo-400">Next:</span> {getBenefits(skill.level + 1)}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}